import { useContext } from "react";
import { ITaskItemProps, TaskItemList } from "./TaskItemList";
import { GuarrantorDetailViewContext } from "../../Private";
import { useNavigate } from "react-router-dom";
import { TaskState } from "gql/graphql";
import { LOAN_PHASES, Phases } from "../../LoanDetails/LoanPhase.constanst";
import { RulesTasksCards } from "../../LoanDetails/RulesTask.constanst";

export type LoanStatus = 'review' | 'approval';
export interface ILoanListItemProps {
  _id: string;
  owner: any;
  progress: any;
  name: string;
  type?: string;
  purpose?: string;
  tasks: ITaskItemProps[];
  onClick?: () => void;
}

const PHASE_LABEL: Record<string, string> = LOAN_PHASES.reduce((acc, p) => {
  acc[p.key] = p.name;
  return acc;
}, {} as Record<string, string>);

const getBorrowerTasksForPhase = (loan: { type?: string; purpose?: string; tasks: any[] }, phaseKey: Phases) => {
  const matchingRules = RulesTasksCards.filter((rule) =>
    rule.phaseProgress === phaseKey &&
    (!rule.loanType || rule.loanType.includes(loan.type as any)) &&
    (!rule.loanPurposes || rule.loanPurposes.includes(loan.purpose as any))
  );

  return matchingRules
    .map((rule) => loan.tasks?.find((t: any) => t?.key === rule.key && !t?.hidden))
    .filter(Boolean);
};

const isBorrowerPhaseComplete = (loan: { type?: string; purpose?: string; tasks: any[] }, phaseKey: Phases) => {
  const phaseTasks = getBorrowerTasksForPhase(loan, phaseKey);
  if (phaseTasks.length === 0) return true;
  return phaseTasks.every((t: any) => t.state === TaskState.Approved);
};

const isBackendPhaseComplete = (progress: any, phaseKey: Phases) => {
  const phase = progress?.find((p: any) => p.key === phaseKey);
  return phase?.state === 'complete';
};

const getCurrentPhaseLabel = (loan: { type?: string; purpose?: string; tasks: any[]; progress: any }): string => {
  // APPROVAL: combina tasks del borrower + estado backend (que también considera lender items)
  if (!isBorrowerPhaseComplete(loan, Phases.APPROVAL) || !isBackendPhaseComplete(loan.progress, Phases.APPROVAL)) {
    return PHASE_LABEL[Phases.APPROVAL];
  }

  // DUE_DILIGENCE: idem
  if (!isBorrowerPhaseComplete(loan, Phases.DUE_DILIGENCE) || !isBackendPhaseComplete(loan.progress, Phases.DUE_DILIGENCE)) {
    return PHASE_LABEL[Phases.DUE_DILIGENCE];
  }

  // FINAL/CLOSING: dependen de loanDocs/salesforce → confiamos en backend progress
  if (!isBackendPhaseComplete(loan.progress, Phases.FINAL)) return PHASE_LABEL[Phases.FINAL];
  if (!isBackendPhaseComplete(loan.progress, Phases.CLOSING)) return PHASE_LABEL[Phases.CLOSING];

  return PHASE_LABEL[Phases.CLOSING];
};

export const LoanListItem: React.FC<ILoanListItemProps> = (props) => {
  const navigate = useNavigate();
  const { setHandlerVisible: setBorrower }: any = useContext(GuarrantorDetailViewContext);

  const { owner, name, progress, tasks, type, purpose, onClick } = props;

  const redirectBorrower = (ownerId: string) => {
    setBorrower(ownerId);
    navigate('/borrowers');
  }

  const stateNow = getCurrentPhaseLabel({ type, purpose, tasks: tasks as any[], progress });
  
  return (
    <div className='loan-list-item'>
      <div className='loan-status-badge-container' onClick={onClick}>
        <div className='loan-status-badge'>
          {stateNow}
        </div>
      </div>
      <div className='borrower-summary-container' onClick={() => redirectBorrower(owner._id)}>
        <div className='borrower-summary'>
          <div className='caption'>Borrower</div>
          <div className='borrower-name'>{owner.name + ' ' + owner.lastname}</div>
        </div>
      </div>
      <div>
        <div className='address-summary' onClick={onClick}>
          <div className='caption'>Address</div>
          <div className='address'>{name}</div>
        </div>
      </div>
      <div>
        <div className='task-summary'>
          <div className='caption'>Task Completion</div>
          <div className='task-item-container'>
            {tasks?.map((task: any, index) => (
              !task.hidden &&
                <TaskItemList {...task} taskKey={task.key} key={index} handlerOpenDetail={onClick}/>
            ))}
          </div>
        </div>
      </div>
      <div onClick={onClick}>
        <div className='action-container'>
          {"View loan details -->"}
        </div>
      </div>
    </div>
  )
}
